'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { X, Plus, Wallet, CreditCard, Receipt, CalendarPlus, ScanLine } from 'lucide-react';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (action: 'session' | 'transaction' | 'bankroll' | 'expense' | 'scanReceipt') => void;
  /** Déclenche l'ouverture de la caméra de façon synchrone (requis pour iOS). Appelé avant onSelect('scanReceipt'). */
  triggerScan?: () => void;
}

export const QuickAddModal: React.FC<QuickAddModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  triggerScan,
}) => {
  const t = useTranslations('dashboard.quickAdd');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const quickAddOptions = [
    {
      id: 'session' as const,
      icon: CalendarPlus,
      iconBg: 'bg-blue-500',
      title: t('options.session.title'),
      description: t('options.session.description'),
    },
    {
      id: 'transaction' as const,
      icon: Wallet,
      iconBg: 'bg-gold',
      title: t('options.transaction.title'),
      description: t('options.transaction.description'),
    },
    {
      id: 'expense' as const,
      icon: Receipt,
      iconBg: 'bg-orange-500',
      title: t('options.expense.title'),
      description: t('options.expense.description'),
    },
    {
      id: 'bankroll' as const,
      icon: CreditCard,
      iconBg: 'bg-purple-500',
      title: t('options.bankroll.title'),
      description: t('options.bankroll.description'),
    },
    ...(isMobile
      ? [
          {
            id: 'scanReceipt' as const,
            icon: ScanLine,
            iconBg: 'bg-teal-500',
            title: t('options.scanReceipt.title'),
            description: t('options.scanReceipt.description'),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Empêcher le scroll du body quand la modal est ouverte
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl my-auto animate-in fade-in-0 zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <Plus className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label={t('close')}
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('question')}</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {quickAddOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      if (option.id === 'scanReceipt') triggerScan?.();
                      onSelect(option.id);
                      onClose();
                    }}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all group text-left"
                  >
                    <div className={`${option.iconBg} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-0.5 truncate">{option.title}</h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{option.description}</p>
                    </div>
                    <div className="text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer hint */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 rounded-b-xl">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {t.rich('pressEsc', {
                key: () => <kbd className="px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs font-mono text-gray-700 dark:text-gray-300">Esc</kbd>
              })}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

